import { supabase } from './supabase';
import type { Content, ConversationSettings } from '@shared/types';
import type { FileConversation } from '../types/misc';

export const CONVERSATIONS_QUERY_KEY = ['conversations'] as const;
export const CONVERSATIONS_STALE_TIME = 30_000;

export const THUMBNAIL_URLS_QUERY_KEY = (userId: string) =>
  ['thumbnail-urls', userId] as const;
export const THUMBNAIL_URLS_STALE_TIME = 10 * 60_000; // 10 minutes (signed URLs last 1 hour)

// Animated (prebaked WebM loop) thumbnails live alongside the JPEG poster at
// `{userId}/thumbnails/{conversationId}.webm` in the `images` bucket. They're
// resolved by a parallel query (keyed by userId) so a card can lazily mount a
// `<video>` over the existing JPEG poster — first paint stays the JPEG, motion
// is restored without re-bloating it. (#58)
export const THUMBNAIL_VIDEO_URLS_QUERY_KEY = (userId: string) =>
  ['thumbnail-video-urls', userId] as const;
export const THUMBNAIL_VIDEO_URLS_STALE_TIME = 10 * 60_000; // 10 min (signed URLs last 1 hour)

export const THUMBNAIL_VERSION_QUERY_KEY = ['thumbnail-version'] as const;
export const THUMBNAIL_VERSION_STALE_TIME = 60_000;
export const LOCAL_THUMBNAIL_VERSION_KEY = 'tk-thumb-version';

export function makeThumbnailVersionQueryFn() {
  return async (): Promise<string> => {
    const { data } = await supabase
      .from('app_config')
      .select('value')
      .eq('key', 'thumbnail_version')
      .single();
    return data?.value ?? '1';
  };
}

// Lists `{userId}/thumbnails`, signs every object, and returns a
// `conversationId → signedUrl` map for the files whose extension matches `ext`
// (e.g. '.jpg' for posters, '.webm' for the animated loop). One `list` + one
// batched `createSignedUrls` covers both the poster and the video map.
async function signThumbnailsByExt(
  userId: string,
  ext: '.jpg' | '.webm',
): Promise<Record<string, string>> {
  // Explicit limit: storage.list() silently defaults to 100 objects, which
  // would drop thumbnails past ~50 conversations (poster + webm per card).
  // 1000 covers ~500 conversations in one round-trip; signing is already
  // batched below and scales with the actual file count.
  const { data: files, error: listError } = await supabase.storage
    .from('images')
    .list(`${userId}/thumbnails`, {
      limit: 1000,
      sortBy: { column: 'name', order: 'asc' },
    });

  if (listError || !files || files.length === 0) return {};

  const matching = files.filter((f) => f.name.endsWith(ext));
  if (matching.length === 0) return {};

  const paths = matching.map((f) => `${userId}/thumbnails/${f.name}`);
  const { data: signed, error: signError } = await supabase.storage
    .from('images')
    .createSignedUrls(paths, 3600);

  if (signError || !signed) return {};

  const result: Record<string, string> = {};
  for (const item of signed) {
    if (item.signedUrl && item.path) {
      const filename = item.path.split('/').pop() ?? '';
      const conversationId = filename.slice(0, -ext.length);
      if (conversationId) result[conversationId] = item.signedUrl;
    }
  }
  return result;
}

export function makeThumbnailUrlsQueryFn(userId: string) {
  return (): Promise<Record<string, string>> =>
    signThumbnailsByExt(userId, '.jpg');
}

export function makeThumbnailVideoUrlsQueryFn(userId: string) {
  return (): Promise<Record<string, string>> =>
    signThumbnailsByExt(userId, '.webm');
}

export function makeConversationsQueryFn(userId: string) {
  return async (): Promise<FileConversation[]> => {
    // This list query intermittently returned HTTP 500 ("disappearing
    // documents"): it ran 5–10 s and crossed the 8 s `statement_timeout` for the
    // `authenticated` role. Root cause (measured on prod, interleaved A/B):
    //
    //   1. `messagesCount:messages(count)` — PostgREST builds this as a
    //      per-conversation LATERAL `json_agg(count(*))`. The count's *plan* is
    //      ~1.6 ms, but through PostgREST it ran 5–8 s because the FIRST query to
    //      touch the `messages` table after a cache eviction pays cold heap/TOAST
    //      reads — and `messages.content` carries the inline STL/STEP artifacts
    //      (avg 2.5 MB, max 16 MB; 121 MB total). The oversized working set keeps
    //      falling out of cache, so the count repeatedly went cold. Removing the
    //      embed and counting via a separate, scoped query (below) takes ~250 ms.
    //   2. `first_message:messages(content->text)` had no `order`, so PostgREST
    //      returned an arbitrary message — often the artifact-laden assistant
    //      message — and detoasted its multi-MB `content` just to read one key.
    //      Pinning the embed to the oldest `role='user'` message (the original
    //      prompt, ~360 B) keeps it tiny.
    //
    // Measured: previous query 5.1–9.8 s with intermittent 500s; new query
    // ~0.3–2.1 s, no 500s. The durable fix is moving the artifacts out of
    // `content` into Storage (keystone) — then the working set shrinks and no
    // `messages`-touching query goes cold. The UI uses first_message.text only
    // for search filtering, where the user's prompt is the right text anyway.
    const { data, error } = await supabase
      .from('conversations')
      .select(`*, first_message:messages(content->text)`)
      .eq('user_id', userId)
      // Filter + order the EMBED (not the parent): oldest message with role
      // 'user'. This is a filter on the embedded resource, so conversations
      // without a user message still appear (with an empty first_message);
      // it does not inner-join away parent rows.
      .eq('first_message.role', 'user')
      .order('created_at', {
        referencedTable: 'first_message',
        ascending: true,
      })
      .order('updated_at', { ascending: false })
      .order('created_at', { ascending: false })
      .limit(1, { referencedTable: 'first_message' })
      .overrideTypes<Array<{ settings: ConversationSettings }>>();

    if (error) throw error;

    // Message counts, fetched separately and scoped to these conversations.
    // Selecting only `conversation_id` never touches `content`, so it stays
    // fast (~250 ms) regardless of artifact bloat. Tally client-side.
    const conversationIds = data.map((c) => c.id);
    const messageCounts: Record<string, number> = {};
    if (conversationIds.length > 0) {
      const { data: countRows, error: countError } = await supabase
        .from('messages')
        .select('conversation_id')
        .in('conversation_id', conversationIds);
      // A count failure must NOT blank the grid — degrade to 0 badges.
      if (countError) {
        console.warn('[conversations] message count fetch failed', countError);
      } else if (countRows) {
        for (const row of countRows) {
          const cid = (row as { conversation_id: string }).conversation_id;
          messageCounts[cid] = (messageCounts[cid] ?? 0) + 1;
        }
      }
    }

    return data.map((conv) => {
      // With `content->text` the embedded row is `{ text: <string> }` directly
      // (not `{ content: {...} }`). Images are no longer fetched here — they
      // were unused by the list UI.
      const firstRow = conv.first_message?.[0] as
        | { text?: unknown }
        | undefined;
      const firstText = typeof firstRow?.text === 'string' ? firstRow.text : '';

      return {
        ...conv,
        created_at: conv.created_at || new Date().toISOString(),
        updated_at:
          conv.updated_at || conv.created_at || new Date().toISOString(),
        message_count: messageCounts[conv.id] ?? 0,
        first_message: {
          text: firstText,
          images: [],
        } as Content,
      };
    });
  };
}
